import { z } from 'zod'

import { DEFAULT_SUBSCRIPTION_LEVELS, DefaultSubscriptionLevel } from '@forto-dev/notification-center-models'

export const NOTIFICATION_PREFERENCE_TYPES = ['update', 'reassurance', 'deviationRisk', 'deviation'] as const
export const NotificationPreferenceTypeEnum = z.enum(NOTIFICATION_PREFERENCE_TYPES)

export type NotificationPreferenceType = z.infer<typeof NotificationPreferenceTypeEnum>

export const NOTIFICATION_PREFERENCE_SUB_CATEGORIES = [
  'booking',
  'crd',
  'rolledCargo',
  'customs',
  'demdet',
  'miscellaneous',
  'shipmentUpdates',
] as const

export type NotificationPreferenceSubCategory = (typeof NOTIFICATION_PREFERENCE_SUB_CATEGORIES)[number]

export const isValidNotificationPreferenceSubCategory = (input: string): input is NotificationPreferenceSubCategory =>
  NOTIFICATION_PREFERENCE_SUB_CATEGORIES.some((item) => item === input)

export const NOTIFICATION_CHANNELS = ['web', 'instantEmail', 'digestEmail'] as const

export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number]

export type AvailableNotificationPreference = {
  references: string[]
  type: NotificationPreferenceType
  title: string
  subtitle: string
  subCategory?: NotificationPreferenceSubCategory
  visibleChannels?: NotificationChannel[]
  default?: {
    instantEmail: boolean
    digestEmail: boolean
    web: boolean
  }
}
export const NOTIFICATION_SECTION_CATEGORIES = ['preTransport', 'mainCarriage', 'onCarriage', 'other'] as const

export type NotificationSectionCategory = (typeof NOTIFICATION_SECTION_CATEGORIES)[number]

type APINotificationSection = {
  title: string
  category: NotificationSectionCategory
  preferences: AvailableNotificationPreference[]
}

export type APIGetAvailableNotificationPreferencesResult = {
  sections: APINotificationSection[]
}

type APINotificationPreference = {
  reference: string
  instantEmail: boolean
  digestEmail: boolean
  web: boolean
}

export type APIGetCurrentNotificationPreferencesResult = {
  language: string
  timezone?: string | null
  preferences: APINotificationPreference[]
  notifyFor: NotificationPreferencesNotifyFor
  defaultSubscriptionLevel: DefaultSubscriptionLevel
}

export const NOTIFICATION_PREFERENCES_NOTIFY_FOR = ['all', 'subscribedOnly'] as const

export type NotificationPreferencesNotifyFor = (typeof NOTIFICATION_PREFERENCES_NOTIFY_FOR)[number]

export const isValidNotificationPreferencesNotifyFor = (input: string): input is NotificationPreferencesNotifyFor =>
  NOTIFICATION_PREFERENCES_NOTIFY_FOR.some((item) => item === input)

const AvailableNotificationPreferenceSchema = z.object({
  references: z.array(z.string()),
  type: NotificationPreferenceTypeEnum,
  title: z.string(),
  subtitle: z.string(),
  subCategory: z
    .enum(NOTIFICATION_PREFERENCE_SUB_CATEGORIES)
    .nullish()
    .transform((value) => value ?? undefined),
  visibleChannels: z
    .array(z.enum(NOTIFICATION_CHANNELS))
    .nullish()
    .transform((value) => value ?? undefined),
  default: z
    .object({
      instantEmail: z.boolean(),
      digestEmail: z.boolean(),
      web: z.boolean(),
    })
    .nullish()
    .transform((value) => value ?? undefined),
})

const NotificationSectionSchema = z.object({
  title: z.string(),
  category: z.enum(NOTIFICATION_SECTION_CATEGORIES),
  preferences: z.array(AvailableNotificationPreferenceSchema),
})

export type NotificationSection = z.infer<typeof NotificationSectionSchema>

export const GetAvailableNotificationPreferencesResultSchema = z.object({
  sections: z.array(NotificationSectionSchema),
})
export type GetAvailableNotificationPreferencesResult = z.infer<typeof GetAvailableNotificationPreferencesResultSchema>

export const NotificationPreferenceSchema = z.object({
  reference: z.string(),
  instantEmail: z.boolean(),
  digestEmail: z.boolean(),
  web: z.boolean(),
})

export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>

export const GetCurrentNotificationPreferencesResultSchema = z.object({
  language: z.string(),
  timezone: z
    .string()
    .nullish()
    .transform((value) => value ?? undefined),
  preferences: z.array(NotificationPreferenceSchema),
  notifyFor: z.enum(NOTIFICATION_PREFERENCES_NOTIFY_FOR),
  defaultSubscriptionLevel: z.enum(DEFAULT_SUBSCRIPTION_LEVELS).or(z.string()),
})
export type GetCurrentNotificationPreferencesResult = z.infer<typeof GetCurrentNotificationPreferencesResultSchema>
